import type { Pt2 } from './mercator'

/**
 * An ellipse in projected metres. `rotation` is the angle, radians
 * counter-clockwise from +x, of the `a` axis.
 */
export interface Ellipse {
  cx: number
  cy: number
  a: number
  b: number
  rotation: number
}

export function expandEllipse(e: Ellipse, margin: number): Ellipse {
  return { ...e, a: e.a + margin, b: e.b + margin }
}

export function ellipsePoint(e: Ellipse, t: number): Pt2 {
  const xl = e.a * Math.cos(t)
  const yl = e.b * Math.sin(t)
  const cr = Math.cos(e.rotation)
  const sr = Math.sin(e.rotation)
  return [e.cx + cr * xl - sr * yl, e.cy + sr * xl + cr * yl]
}

/** `resolution` points at equal parameter steps, as an open ring. */
export function ellipseRing(e: Ellipse, resolution: number): Pt2[] {
  return Array.from({ length: resolution }, (_, i) => ellipsePoint(e, (2 * Math.PI * i) / resolution))
}

/**
 * Minimum-volume enclosing ellipse by Khachiyan's algorithm, the app's
 * `calculateContainingEllipse` verbatim: the points are centred and scaled to a
 * unit radius first so the tolerance means the same thing at any size, the
 * iteration stops at `tolerance` (in the caller's units) or `maxIterations`,
 * and the result is inflated by whatever tiny factor the last iterate still
 * needed to actually contain every point.
 */
export function containingEllipse(points: readonly Pt2[], tolerance: number, maxIterations: number): Ellipse {
  if (points.length === 0) throw new RangeError('containingEllipse: points must not be empty')
  let cx = 0
  let cy = 0
  for (const [x, y] of points) {
    cx += x
    cy += y
  }
  cx /= points.length
  cy /= points.length
  if (points.length === 1) return { cx, cy, a: 0, b: 0, rotation: 0 }

  let maxR2 = 0
  for (const [x, y] of points) maxR2 = Math.max(maxR2, (x - cx) ** 2 + (y - cy) ** 2)
  const scale = Math.sqrt(maxR2)
  const scaled: Pt2[] = points.map(([x, y]) => [(x - cx) / scale, (y - cy) / scale])
  const e = fitUnit(scaled, tolerance / scale, maxIterations)
  return { cx: e.cx * scale + cx, cy: e.cy * scale + cy, a: e.a * scale, b: e.b * scale, rotation: e.rotation }
}

function fitUnit(points: readonly Pt2[], tolerance: number, maxIterations: number): Ellipse {
  if (points.length === 2) {
    const [[x0, y0], [x1, y1]] = points
    const dx = x1 - x0
    const dy = y1 - y0
    return { cx: (x0 + x1) / 2, cy: (y0 + y1) / 2, a: Math.hypot(dx, dy) / 2, b: 0, rotation: Math.atan2(dy, dx) }
  }
  const d = 2
  const n = points.length
  const qx = points.map((p) => p[0])
  const qy = points.map((p) => p[1])
  const u = new Array<number>(n).fill(1 / n)

  let err = Infinity
  let iter = 0
  while (err > tolerance && iter < maxIterations) {
    iter++
    const xInv = invert3x3Regularized(computeX3x3(qx, qy, u))
    let j = 0
    let maxM = -Infinity
    for (let i = 0; i < n; i++) {
      const mi = quadForm3(xInv, qx[i], qy[i], 1)
      if (mi > maxM) {
        maxM = mi
        j = i
      }
    }
    err = maxM - (d + 1)
    const step = (maxM - (d + 1)) / ((d + 1) * (maxM - 1))
    for (let i = 0; i < n; i++) u[i] *= 1 - step
    u[j] += step
  }

  let cx = 0
  let cy = 0
  for (let i = 0; i < n; i++) {
    cx += u[i] * qx[i]
    cy += u[i] * qy[i]
  }
  let s11 = 0
  let s12 = 0
  let s22 = 0
  for (let i = 0; i < n; i++) {
    const dx = qx[i] - cx
    const dy = qy[i] - cy
    s11 += u[i] * dx * dx
    s12 += u[i] * dx * dy
    s22 += u[i] * dy * dy
  }
  const [i11, i12, i22] = invert2x2Regularized(s11, s12, s22)
  const a11 = i11 / d
  const a12 = i12 / d
  const a22 = i22 / d

  const eig = eigen2x2Symmetric(a11, a12, a22)
  const len1 = 1 / Math.sqrt(Math.max(eig.lambda1, 1e-18))
  const len2 = 1 / Math.sqrt(Math.max(eig.lambda2, 1e-18))
  let a: number
  let b: number
  let rotation: number
  if (len1 >= len2) {
    a = len1
    b = len2
    rotation = eig.theta1
  } else {
    a = len2
    b = len1
    rotation = eig.theta2
  }

  let maxValue = 0
  for (let i = 0; i < n; i++) {
    const dx = qx[i] - cx
    const dy = qy[i] - cy
    maxValue = Math.max(maxValue, a11 * dx * dx + 2 * a12 * dx * dy + a22 * dy * dy)
  }
  if (maxValue > 1) {
    const inflate = Math.sqrt(maxValue) * (1 + 1e-12)
    a *= inflate
    b *= inflate
  }
  return { cx, cy, a, b, rotation }
}

function computeX3x3(x: number[], y: number[], u: number[]): number[] {
  let x11 = 0, x12 = 0, x13 = 0, x22 = 0, x23 = 0, x33 = 0
  for (let i = 0; i < u.length; i++) {
    const w = u[i]
    x11 += w * x[i] * x[i]
    x12 += w * x[i] * y[i]
    x13 += w * x[i]
    x22 += w * y[i] * y[i]
    x23 += w * y[i]
    x33 += w
  }
  return [x11, x12, x13, x12, x22, x23, x13, x23, x33]
}

function quadForm3(m: number[], x: number, y: number, w: number): number {
  const t0 = m[0] * x + m[1] * y + m[2] * w
  const t1 = m[3] * x + m[4] * y + m[5] * w
  const t2 = m[6] * x + m[7] * y + m[8] * w
  return x * t0 + y * t1 + w * t2
}

function invert3x3(a: number[]): number[] | null {
  const [a00, a01, a02, a10, a11, a12, a20, a21, a22] = a
  const c00 = a11 * a22 - a12 * a21
  const c01 = -(a10 * a22 - a12 * a20)
  const c02 = a10 * a21 - a11 * a20
  const c10 = -(a01 * a22 - a02 * a21)
  const c11 = a00 * a22 - a02 * a20
  const c12 = -(a00 * a21 - a01 * a20)
  const c20 = a01 * a12 - a02 * a11
  const c21 = -(a00 * a12 - a02 * a10)
  const c22 = a00 * a11 - a01 * a10
  const det = a00 * c00 + a01 * c01 + a02 * c02
  if (!Number.isFinite(det) || Math.abs(det) < 1e-18) return null
  const inv = 1 / det
  return [c00 * inv, c10 * inv, c20 * inv, c01 * inv, c11 * inv, c21 * inv, c02 * inv, c12 * inv, c22 * inv]
}

/** Degenerate point sets (all on a line) leave the moment matrix singular; the
 *  app nudges the diagonal twice before giving up, and so does this. */
function invert3x3Regularized(a: number[]): number[] {
  const direct = invert3x3(a)
  if (direct) return direct
  const b = [...a]
  for (const eps of [1e-14, 1e-10]) {
    b[0] += eps
    b[4] += eps
    b[8] += eps
    const inv = invert3x3(b)
    if (inv) return inv
  }
  throw new Error('containingEllipse: degenerate point set (3x3 moment matrix not invertible)')
}

function invert2x2(a: number, b: number, c: number): [number, number, number] | null {
  const det = a * c - b * b
  if (!Number.isFinite(det) || Math.abs(det) < 1e-18) return null
  return [c / det, -b / det, a / det]
}

function invert2x2Regularized(a: number, b: number, c: number): [number, number, number] {
  return (
    invert2x2(a, b, c) ??
    invert2x2(a + 1e-14, b, c + 1e-14) ??
    invert2x2(a + 1e-10, b, c + 1e-10) ??
    (() => {
      throw new Error('containingEllipse: collinear points (2x2 scatter matrix not invertible)')
    })()
  )
}

function eigen2x2Symmetric(a: number, b: number, c: number) {
  const tr = a + c
  const det = a * c - b * b
  const s = Math.sqrt(Math.max(0, tr * tr - 4 * det))
  const lambda1 = (tr - s) / 2
  const lambda2 = (tr + s) / 2
  const angleFor = (lambda: number) => {
    if (Math.abs(b) > 1e-18) return Math.atan2(lambda - a, b)
    return Math.abs(a - lambda) <= Math.abs(c - lambda) ? 0 : Math.PI / 2
  }
  return { lambda1, lambda2, theta1: angleFor(lambda1), theta2: angleFor(lambda2) }
}
